// @flow

import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { i18n, getCurrencySymbol } from 'shared/utils';
import { PrintPage } from 'shared/components/common/PrintLayout';
import {
  buildBomPrintReport,
  formatCost,
  formatRowCell,
  type BomPrintParentInfo,
  type BomPrintReportType,
} from './bomPrintReports';

const FishbowlPrintPage = styled(PrintPage)`
  font-size: 11px;
  color: #000;
  padding: 0 4px;
`;

const TopHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
`;

const ReportTitleBlock = styled.div`
  flex: 1;
`;

const ReportTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  line-height: 1.2;
`;

const ReportSubtitle = styled.div`
  font-size: 11px;
  margin-top: 2px;
`;

const CompanyName = styled.div`
  font-size: 12px;
  font-weight: bold;
  text-align: right;
  text-decoration: underline;
  max-width: 45%;
  line-height: 1.3;
`;

const HeaderRule = styled.div`
  border-bottom: 2px solid #000;
  margin: 6px 0 8px;
`;

const MetaTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 4px;
  font-size: 10px;

  th {
    text-align: left;
    font-weight: bold;
    padding: 2px 8px 2px 0;
    vertical-align: bottom;
    white-space: nowrap;
  }

  td {
    padding: 2px 8px 6px 0;
    vertical-align: top;
  }
`;

const MetaRule = styled.div`
  border-bottom: 1px solid #000;
  margin-bottom: 6px;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 10px;

  thead tr {
    border-top: 1px solid #000;
    border-bottom: 1px solid #000;
  }

  th {
    font-weight: bold;
    padding: 4px 6px 4px 0;
    text-align: left;
    vertical-align: bottom;
    white-space: normal;
  }

  th.align-right,
  td.align-right {
    text-align: right;
    padding-right: 0;
    padding-left: 8px;
  }

  td {
    padding: 3px 6px 3px 0;
    vertical-align: top;
    text-align: left;
  }

  tbody tr:last-child td {
    padding-bottom: 6px;
  }
`;

const IndentedItemCode = styled.td`
  padding-left: ${p => 4 + (p.indentLevel || 0) * 10}px !important;
`;

const FooterNotes = styled.div`
  margin-top: 12px;
  font-size: 10px;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const TotalRow = styled.tr`
  td {
    border-top: 1px solid #000;
    font-weight: bold;
    padding-top: 6px;
  }
`;

const BottomRule = styled.div`
  border-bottom: 2px solid #000;
  margin-top: 8px;
`;

type Props = {
  reportType: BomPrintReportType,
  treeData: ?Array<Object>,
  parent: BomPrintParentInfo,
};

const BomFishbowlPrint = ({ reportType, treeData, parent }: Props) => {
  const report = buildBomPrintReport(reportType, treeData);
  const parentDescription =
    parent.description || parent.name || '';
  const billNumber = parent.bomNumber || parent.itemNumber;

  return (
    <FishbowlPrintPage>
      <TopHeader>
        <ReportTitleBlock>
          <ReportTitle>{i18n.t(report.reportTitle)}</ReportTitle>
          {report.reportSubtitle ? (
            <ReportSubtitle>{i18n.t(report.reportSubtitle)}</ReportSubtitle>
          ) : null}
        </ReportTitleBlock>
        {parent.companyName ? (
          <CompanyName>{parent.companyName}</CompanyName>
        ) : null}
      </TopHeader>

      <HeaderRule />

      <MetaTable>
        <thead>
          <tr>
            <th>{i18n.t('Bill Number')}</th>
            <th>{i18n.t('Type')}</th>
            <th>{i18n.t('Unit of Measure')}</th>
            <th>{i18n.t('Description')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{billNumber}</td>
            <td>{parent.bomTypeLabel}</td>
            <td>{parent.uom}</td>
            <td>{parentDescription}</td>
          </tr>
        </tbody>
      </MetaTable>

      <MetaRule />

      <DataTable>
        <thead>
          <tr>
            {report.columns.map(col => (
              <th
                key={col.key}
                className={col.align === 'right' ? 'align-right' : undefined}
              >
                {i18n.t(col.label)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.rows.map((row, index) => (
            <tr key={`${row.itemNumber}-${index}`}>
              {report.columns.map(col => {
                const value = formatRowCell(row, col.key);
                const isIndentedCode =
                  reportType === 'indented' && col.key === 'itemNumber';

                if (isIndentedCode) {
                  return (
                    <IndentedItemCode
                      key={col.key}
                      indentLevel={row.level}
                      className={col.align === 'right' ? 'align-right' : undefined}
                    >
                      {value}
                    </IndentedItemCode>
                  );
                }

                return (
                  <td
                    key={col.key}
                    className={col.align === 'right' ? 'align-right' : undefined}
                  >
                    {value}
                  </td>
                );
              })}
            </tr>
          ))}
          {reportType === 'costed' && report.totalCost != null && (
            <TotalRow>
              {report.columns.map(col => {
                if (col.key === 'averageCost') {
                  return (
                    <td key={col.key} className="align-right">
                      {i18n.t('Total:')}
                    </td>
                  );
                }
                if (col.key === 'costOfBill') {
                  return (
                    <td key={col.key} className="align-right">
                      {getCurrencySymbol()}
                      {formatCost(report.totalCost)}
                    </td>
                  );
                }
                return <td key={col.key} />;
              })}
            </TotalRow>
          )}
        </tbody>
      </DataTable>

      {report.footerNotes.length > 0 && (
        <FooterNotes>
          {report.footerNotes.map(note => (
            <div key={note}>{note}</div>
          ))}
        </FooterNotes>
      )}

      <BottomRule />

      <div style={{ fontSize: '9px', marginTop: '8px', textAlign: 'right' }}>
        {moment().format('MM/DD/YYYY')}
      </div>
    </FishbowlPrintPage>
  );
};

export default BomFishbowlPrint;
